import { Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { GeneralUpgradeModalComponent } from '@misc/upsell/general-upgrade-modal.component';
import { UPSELL_CONFIG, CAMPAIGN_MAP, FEATURE_FLAG_UPSELL_CONFIG_KEYS } from '@misc/upsell/upsell-config';

import { FeatureUpgradeService } from './upgrade.service';
import { Session } from '@services/session';
import { FleetGrowthFeatureFlagService } from '@services/feature-flags/growth';
import { setCookie, getCookie } from '@lib/cookies';
import { CookieKey } from '@lib/cookie-keys.const';
import { mapValueToBoolean } from '@lib/utils';

const UPSELL_ENTRY_POPUP_DB_CTA_KEY: string = 'upsell_entry_campaign_cta';

@Component( {
  selector: 'upsell-entry-popup',
  templateUrl: './upsell-entry-popup.component.html',
  styleUrls: [ './upsell-entry-popup.component.scss' ],
} )

export class UpsellEntryPopupComponent implements OnInit, OnDestroy {
  @ViewChild('upsellEntryPopup', { static: false }) public upsellEntryPopup: GeneralUpgradeModalComponent;
  public isPopupFFEnabled: boolean;
  public isPopupVisible: boolean;
  public isEOQUpsellQ12023Enabled: boolean;
  public featureServiceSubscription: Subscription;
  public redirectServiceSubscription: Subscription;
  public ctaContentServiceSubscription: Subscription;
  public requestServiceSubscription: Subscription;
  private unsubscribe = new Subject<void>();
  private isPopupRun: number;

  constructor(
    public session: Session,
    private featureUpgradeService: FeatureUpgradeService,
    private fgFeatureFlagService: FleetGrowthFeatureFlagService,
  ) {
    // upsell entry popup only works via FF
    this.isPopupRun = getCookie(CookieKey.UPSELL_ENTRY_POPUP_RUN) ? parseInt(getCookie(CookieKey.UPSELL_ENTRY_POPUP_RUN), 10) : 0;
    this.isPopupFFEnabled = !!fgFeatureFlagService.getUpsellEntryPopupVariation();
    this.isEOQUpsellQ12023Enabled = fgFeatureFlagService.isEOQUpsellQ12023Enabled();
    this.isPopupVisible = this.getPopupVisibility();
  }

  public ngOnInit(): void {
    this.dismissalSubscription();
    this.ctaContentSubscription();
    this.requestReceivedSubscription();
    this.redirectSubscription();
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public ngAfterViewInit(): void {
    if (this.isPopupVisible) {
      this.upsellEntryPopup.show(null);

      if (this.isEOQUpsellQ12023Enabled) {
        if ( getCookie(CookieKey.UPSELL_ENTRY_POPUP_RUN) ) {
          setCookie(CookieKey.UPSELL_ENTRY_POPUP_RUN, parseInt(getCookie(CookieKey.UPSELL_ENTRY_POPUP_RUN), 10) + 1);
        } else {
          setCookie(CookieKey.UPSELL_ENTRY_POPUP_RUN, 1);
        }
      }
    }
  }

  public get getFlagVariation(): string {
    if (this.isPopupFFEnabled) {
      return this.fgFeatureFlagService.getUpsellEntryPopupVariation();
    } else {
      if (this.isEOQUpsellQ12023Enabled) {
        return FEATURE_FLAG_UPSELL_CONFIG_KEYS.EOQ_Q1_2023_UPSELL;
      } else {
        return '';
      }
    }
  }

  private requestReceivedSubscription(): void {
    this.requestServiceSubscription = this.featureUpgradeService.requestReceived$
    .pipe(takeUntil(this.unsubscribe))
    .subscribe((): void => {
      this.upsellEntryPopup.hide(null);
    });
  }

  // set cookie setting to ensure entry popup stays hidden once dismissed
  private dismissalSubscription(): void {
    this.featureServiceSubscription = this.featureUpgradeService.featureModalHide$
    .pipe(takeUntil(this.unsubscribe))
    .subscribe((): void => {
      if (this.isEOQUpsellQ12023Enabled) {
        const cookieValue = JSON.parse(getCookie(CookieKey.DASHBOARD_STORAGE));
        cookieValue.upsell_eoq_q1_2023 = true;
        const updatedCookieValue = JSON.stringify(cookieValue);
        setCookie(CookieKey.DASHBOARD_STORAGE, updatedCookieValue);
      }
      setCookie(CookieKey.UPSELL_ENTRY_POPUP_HIDE, true, { expires: 3 });
    });
  }

  // learn more/redirect click event listener
  private redirectSubscription(): void {
    this.redirectServiceSubscription = this.featureUpgradeService.featureModalUrlRedirect$
    .pipe(takeUntil(this.unsubscribe))
    .subscribe((): void => {
      this.upsellEntryPopup.hide(null);
    });
  }

  private ctaContentSubscription(): void {
    this.ctaContentServiceSubscription = this.featureUpgradeService.ctaContentContext$
    .pipe(takeUntil(this.unsubscribe))
    .subscribe((ctaContent): void => {
      localStorage.setItem(UPSELL_ENTRY_POPUP_DB_CTA_KEY, ctaContent);
    });
  }

  private getPopupVisibility(): boolean {
    return (this.isPopupFFEnabled && this.isValidFeatureKey
      && this.isShowEntryPopupCookie && this.isCampaignNotRequested) ||
      (this.isPopRunThisSession && this.isEOQUpsellQ12023Enabled && !this.session.isSweepStakesRequested && this.isPopupRun <= 2);
  }

  private get isValidFeatureKey(): boolean {
    return UPSELL_CONFIG.hasOwnProperty(this.getFlagVariation);
  }

  private get isShowEntryPopupCookie(): boolean {
    return !mapValueToBoolean(getCookie(CookieKey.UPSELL_ENTRY_POPUP_HIDE));
  }

  private get isCampaignNotRequested(): boolean {
    const campaignType: string = this.getFlagVariation ? CAMPAIGN_MAP[this.getFlagVariation] : '';
    const isCampaignRequested: boolean = campaignType ? !!this.session.getUpgradeStatus()[campaignType] : true;

    // Edge case: campaign isn't considered to be requested if it was requested via Learn more cta
    return !isCampaignRequested || this.isCampaignRequestedViaLearnMore;
  }

  private get isCampaignRequestedViaLearnMore(): boolean {
    return [undefined, null, 'Learn more'].indexOf(localStorage.getItem(UPSELL_ENTRY_POPUP_DB_CTA_KEY)) > -1;
  }

  private get isPopRunThisSession(): boolean {
    const cookieValue = JSON.parse(getCookie(CookieKey.DASHBOARD_STORAGE) || '{}');
    return !cookieValue.upsell_eoq_q1_2023;
  }
}
